const normalize = str => str.toLowerCase().trim()

// Client-side match against each lens's search_keywords array, returned in the
// same { categoryName, filteredLenses } shape as the server name search.
// When a keyword uniquely identifies a single variant of a lens (e.g. "fourteen"
// only appears on the 14mm/T6 copy), that variant's focalLength/transmission are
// attached so clicking the result loads that exact variant instead of the default.
export const searchLensesByKeyword = (categorizedData, query) => {
  if (!query || !Array.isArray(categorizedData)) return []

  const normQuery = normalize(query)

  const results = []
  categorizedData.forEach(category => {
    const matchesByName = {}

    ;(category.lenses || []).forEach(lens => {
      const keywords = Array.isArray(lens.search_keywords) ? lens.search_keywords : []
      const isMatch = keywords.some(keyword => normalize(keyword).includes(normQuery))
      if (!isMatch) return

      if (!matchesByName[lens.name]) matchesByName[lens.name] = []
      matchesByName[lens.name].push(lens)
    })

    const filteredLenses = Object.entries(matchesByName).map(([name, variants]) => {
      const isNew = variants.some(variant => variant.isNew)
      const isPremium = variants.some(variant => variant.isPremium)

      if (variants.length === 1) {
        return {
          name,
          isNew,
          isPremium,
          focalLength: variants[0].focalLength,
          transmission: variants[0].transmission
        }
      }

      // A keyword matched several variants of this lens family. If the family's
      // default variant is one of the matches, let the app's default-fallback
      // handle it; otherwise that fallback would load a variant that never
      // matched the keyword at all, so pin the first matched variant instead.
      const defaultVariant = variants.find(variant => variant.defaultLeft || variant.defaultRight)
      if (defaultVariant) {
        return { name, isNew, isPremium }
      }

      return {
        name,
        isNew,
        isPremium,
        focalLength: variants[0].focalLength,
        transmission: variants[0].transmission
      }
    })

    if (filteredLenses.length > 0) {
      results.push({ categoryName: category.name, filteredLenses })
    }
  })

  return results
}

// Merges multiple { categoryName, filteredLenses } arrays, de-duplicating lenses
// by name within the same category and preserving the isNew flag and any pinned
// focalLength/transmission (first match found wins).
export const mergeCategorizedResults = (...resultSets) => {
  const merged = {}
  const order = []

  resultSets.forEach(set => {
    const entries = set || []
    entries.forEach(({ categoryName, filteredLenses }) => {
      if (!merged[categoryName]) {
        merged[categoryName] = {}
        order.push(categoryName)
      }
      filteredLenses.forEach(lens => {
        const existing = merged[categoryName][lens.name]
        merged[categoryName][lens.name] = {
          name: lens.name,
          isNew: !!existing?.isNew || !!lens.isNew,
          isPremium: existing?.isPremium ?? lens.isPremium,
          focalLength: existing?.focalLength ?? lens.focalLength,
          transmission: existing?.transmission ?? lens.transmission
        }
      })
    })
  })

  return order.map(categoryName => ({
    categoryName,
    filteredLenses: Object.values(merged[categoryName])
  }))
}

// Selects a search result: when a specific focalLength/transmission was pinned
// (a keyword uniquely matched one variant) it loads that exact variant, otherwise
// it selects by name only and lets the app fall back to its default variant.
export const selectSearchResult = (store, lens, { side } = {}) => {
  const baseId = lens.name.toLowerCase().replace(/\s+/g, '-')

  if (side === 'LEFT') {
    if (lens.focalLength) store.setLeftFocalLength(lens.focalLength)
    if (lens.transmission) store.setLeftTransmission(lens.transmission)
    store.setLeftSelectedVideoId(`${baseId}-left`)
  } else if (side === 'RIGHT') {
    if (lens.focalLength) store.setRightFocalLength(lens.focalLength)
    if (lens.transmission) store.setRightTransmission(lens.transmission)
    store.setRightSelectedVideoId(`${baseId}-right`)
  } else {
    if (lens.focalLength) store.setSelectedFocalLength(lens.focalLength)
    if (lens.transmission) store.setSelectedTransmission(lens.transmission)
    store.setSelectedVideo(baseId)
  }
}
